import React from 'react';
import { 
  Box, 
  Container, 
  Paper, 
  Typography, 
  Link,
  useTheme
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import RegisterForm from '@/components/auth/RegisterForm';
import { useAuth } from '@/contexts/AuthContext';
import { RegisterData } from '@/services/api/domains/auth';
import { formInputStyles, formContainerStyles } from '@/styles/formStyles';
import { useToast } from '@/components/common/Toast';

const RegisterPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const theme = useTheme();
  const { register: registerUser } = useAuth();

  const handleRegister = async (data: RegisterData) => {
    try {
      await registerUser(data);
      toast.showSuccess('Registration successful! Please log in.');
      navigate('/login');
    } catch (error) {
      toast.showError(error instanceof Error ? error.message : 'Registration failed');
    }
  };

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh' }}>
      <Box
        component="img"
        src="/src/assets/logos/DreamzConnectWhite.svg"
        alt="Dreamz Connect"
        onClick={() => navigate('/')}
        sx={{
          position: 'absolute',
          top: 24,
          left: 24,
          height: 20,
          cursor: 'pointer',
          transition: 'opacity 0.2s',
          '&:hover': { opacity: 0.8 }
        }}
      />
      <Box
        sx={{
          minHeight: '100vh',
          width: '100vw',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          p: 2,
          ...formInputStyles(theme),
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={24}
            sx={formContainerStyles(theme)}
          >
            <Box textAlign="center" mb={4}>
              <Typography 
                variant="h4" 
                component="h1" 
                gutterBottom
                fontWeight="bold"
                color="primary"
              >
                Create Account
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Already have an account?{' '}
                <Link 
                  href="/login" 
                  underline="hover"
                  sx={{ 
                    fontWeight: 500,
                    '&:hover': { color: 'primary.main' }
                  }}
                >
                  Sign in
                </Link>
              </Typography>
            </Box>
            <RegisterForm onSubmit={handleRegister} />
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default RegisterPage;